import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, PAGE_PRESETS, createDefaultPage, pageMasterOf } from '../../context/EditorContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from '../ui/alert-dialog';
import { StaticPage } from '../editor/ObjectRenderer';
import { PublicationPreview } from '../editor/PublicationPreview';
import { folderColorCss } from '../../lib/avatar';
import { childrenOf, folderCounts, descendantIds, pathOf } from '../../lib/folderTree';
import { RecentActivity } from '../editor/RecentActivity';
import { SharedBadge } from '../editor/SharedBadge';
import { TEMPLATES, buildTemplateProject } from '../../lib/templates';
import { listProjects, deleteProject, apiErrorMessage, getShare, enableShare, disableShare, shareUrlFor, listFolders, restoreFromTrash } from '../../lib/projectApi';
import { getPinnedIds, togglePinned, getRecents, forgetRecent, relativeTime, greeting } from '../../lib/recents';
import { PAPER_SIZES, DEFAULT_PRINT_OPTIONS, resolvePrintPages, resolvePrintSheets, placementFor, printPublication, printToPdf, parsePageRange, effectivePage, printMarks, effectiveLayout } from '../../lib/printUtils';
import { SheetView } from '../../lib/sheetRender';
import { foldGuidesOf, BLEED_OPTIONS, pageRenderSize } from '../../lib/renderUtils';
import { PrintPreviewGuides, PreviewLegend, loadPreviewLayers, savePreviewLayers } from './PrintPreviewGuides';
import { preflightPage, preflightMessages } from '../../lib/printChecks';
import { usePublicationActions } from '../../hooks/usePublicationActions';
import { SaveAsTemplateDialog } from '../dialogs/SaveAsTemplateDialog';
import {
  Pin, PinOff, FilePlus2, FolderOpen, Import, Printer, Link2, Mail, FileText, FileImage, FileJson, Copy, Check, ChevronLeft, ChevronRight,
  Save, Loader2, LayoutTemplate, Trash2, ExternalLink, AlertTriangle, Cloud, CloudOff, Clock, BookmarkPlus, FileDown, Info, CheckCircle2, Folder, Inbox, FolderTree,
} from 'lucide-react';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/* Shared bits                                                          */
/* ------------------------------------------------------------------ */

export const Section = ({ title, description, children, testId, wide = false }) => (
  <div className={wide ? 'max-w-6xl' : 'max-w-4xl'} data-testid={testId}>
    <h1 className="font-outfit text-2xl font-semibold text-foreground">{title}</h1>
    {description && <p className="text-sm text-muted-foreground mt-1 mb-6">{description}</p>}
    {children}
  </div>
);

/** Card-style action button used across the File tab (Publisher's big "backstage" buttons). */
const ActionCard = ({ icon: Icon, title, description, onClick, disabled, busy, testId, tone = 'default' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || busy}
    className={`w-full rounded-lg border bg-card p-4 text-left transition-[border-color,box-shadow] duration-150 hover:border-primary hover:shadow-md disabled:opacity-60 disabled:hover:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tone === 'primary' ? 'border-primary/50' : 'border-border'}`}
    data-testid={testId}
  >
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}</div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        {description && <div className="text-xs text-muted-foreground mt-0.5">{description}</div>}
      </div>
    </div>
  </button>
);

const ScaledPage = ({ page, master, textFlow, width, testId }) => {
  const scale = width / page.width;
  return (
    <div className="bg-white overflow-hidden" style={{ width: Math.round(page.width * scale), height: Math.round(page.height * scale) }} data-testid={testId}>
      <StaticPage page={page} master={master} scale={scale} textFlow={textFlow} />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Home landing                                                         */
/* ------------------------------------------------------------------ */

const HOME_TEMPLATE_IDS = ['flyer-event', 'newsletter-classic', 'card-minimal', 'postcard-announce', 'flyer-sale'];

export const HomeSection = ({ go, onDone }) => {
  const { state, dispatch, hasUnsavedChanges } = useEditor();
  const { openFromCloud } = usePublicationActions();
  const [projects, setProjects] = useState(null);
  const [folders, setFolders] = useState([]);
  const [error, setError] = useState(null);
  const [pins, setPins] = useState(getPinnedIds());
  const [recents, setRecents] = useState(getRecents());
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState(() => (getPinnedIds().length ? 'pinned' : 'recent'));

  const load = async () => {
    setError(null);
    try {
      const [ps, fs] = await Promise.all([listProjects(), listFolders().catch(() => [])]);
      setProjects(ps); setFolders(fs);
    } catch (e) { setError(await apiErrorMessage(e, 'Could not load your publications.')); setProjects([]); }
  };
  useEffect(() => { load(); }, []);
  // a restore from the Trash / an import changes the library while Home is open
  useEffect(() => { const h = () => load(); window.addEventListener('wp:library-changed', h); return () => window.removeEventListener('wp:library-changed', h); }, []);
  const { direct: directCounts, deep: deepCounts } = useMemo(() => folderCounts(folders, projects), [folders, projects]);
  const topFolders = useMemo(() => childrenOf(folders, null), [folders]);

  const templates = useMemo(() => {
    const picks = HOME_TEMPLATE_IDS.map((id) => TEMPLATES.find((t) => t.id === id)).filter(Boolean);
    const list = picks.length ? picks : TEMPLATES.slice(0, 5);
    return list.map((t) => ({ template: t, project: buildTemplateProject(t, { schemeId: 'office', fontSchemeId: 'modern', info: {} }) }));
  }, []);

  const byId = useMemo(() => Object.fromEntries((projects || []).map((p) => [p.id, p])), [projects]);
  const recentList = useMemo(() => {
    if (!projects) return null;
    const openedAt = Object.fromEntries(recents.map((r) => [r.id, r.openedAt]));
    return [...projects]
      .map((p) => ({ ...p, lastTouched: [openedAt[p.id], p.updated_at].filter(Boolean).sort().pop() || '' }))
      .sort((a, b) => (a.lastTouched < b.lastTouched ? 1 : -1))
      .slice(0, 8);
  }, [projects, recents]);
  const pinnedList = useMemo(() => (projects ? pins.map((id) => byId[id]).filter(Boolean) : null), [projects, pins, byId]);

  const open = async (p) => {
    setBusyId(p.id);
    const full = await openFromCloud(p.id);
    setBusyId(null);
    if (full) { setRecents(getRecents()); onDone(); }
  };
  const pin = (e, id) => { e.stopPropagation(); const next = togglePinned(id); setPins(next); toast.success(next.includes(id) ? 'Pinned to Home' : 'Unpinned'); };
  const undoRemove = async (p) => {
    try { await restoreFromTrash({ projects: [p.id] }); await load(); window.dispatchEvent(new CustomEvent('wp:library-changed', { detail: { source: 'restore' } })); toast.success(`Restored “${p.name}”`, { id: `trash-${p.id}` }); } catch (err) { toast.error(await apiErrorMessage(err, 'Could not restore it.')); }
  };
  const remove = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Move “${p.name}” to the Trash? You can restore it from File ▸ Trash for 30 days.`)) return;
    try {
      await deleteProject(p.id); setProjects((list) => list.filter((x) => x.id !== p.id)); setRecents(forgetRecent(p.id)); if (pins.includes(p.id)) setPins(togglePinned(p.id));
      toast.success(`“${p.name}” moved to the Trash`, { description: 'Kept for 30 days.', action: { label: 'Undo', onClick: () => undoRemove(p) }, id: `trash-${p.id}` });
    } catch (err) { toast.error(await apiErrorMessage(err)); }
  };
  const createBlank = (key) => {
    const p = PAGE_PRESETS[key];
    dispatch({ type: 'SET_PROJECT', payload: { id: null, name: 'Untitled Publication', pages: [createDefaultPage(p.width, p.height, 'Page 1')], canvas_unit: 'in', grid_visible: false, grid_spacing: 20, guides: [] } });
    toast.success(`New ${p.name.split(' (')[0]} publication`);
    onDone();
  };

  const NewCard = ({ label, sub, onClick, children, testId }) => (
    <button type="button" onClick={onClick} className="group shrink-0 w-[132px] rounded-lg border border-border bg-card p-2.5 text-left hover:border-primary hover:shadow-md transition-[border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={testId}>
      <div className="h-[118px] flex items-center justify-center rounded-md bg-muted/40 overflow-hidden">{children}</div>
      <div className="mt-2 text-[12px] font-medium text-foreground truncate">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
    </button>
  );

  const ProjectRow = ({ p }) => {
    const pf = p.folder_id ? folders.find((f) => f.id === p.folder_id) : null; // the folder's colour rides along on the row
    return (
    <div role="button" tabIndex={0} onClick={() => open(p)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(p); } }} className="group flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/70 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" style={pf ? { boxShadow: `inset 3px 0 0 0 ${folderColorCss(pf.color)}` } : undefined} data-folder={pf ? pf.id : undefined} data-testid={`home-project-${p.id}`}>
      <div className="h-[72px] w-[64px] shrink-0 flex items-center justify-center rounded-md bg-muted/40 ml-1" style={pf ? { background: `color-mix(in srgb, ${folderColorCss(pf.color)} 10%, transparent)` } : undefined}><PublicationPreview project={p} maxW={58} maxH={66} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground flex items-center gap-2 min-w-0"><span className="truncate">{p.name}</span><SharedBadge sharedFrom={p.shared_from} size="xs" testId={`home-project-shared-${p.id}`} />{busyId === p.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />}</div>
        <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
          {pf && <button type="button" onClick={(e) => { e.stopPropagation(); go('open', { folderId: pf.id }); }} className="inline-flex items-center gap-1 shrink-0 max-w-[40%] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm" title={`In ${pathOf(folders, pf.id)} — open the folder`} data-testid={`home-project-folder-${p.id}`}><span className="h-2 w-2 rounded-full shrink-0" style={{ background: folderColorCss(pf.color) }} aria-hidden="true" /><span className="truncate">{pf.name}</span><span aria-hidden="true">·</span></button>}
          <Clock className="h-3 w-3 shrink-0" /> <span className="truncate">{relativeTime(p.lastTouched || p.updated_at)} · {p.pages?.length || 0} page{(p.pages?.length || 0) === 1 ? '' : 's'}</span>
        </div>
      </div>
      <button type="button" onClick={(e) => pin(e, p.id)} className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${pins.includes(p.id) ? 'text-primary' : 'text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-foreground'}`} aria-label={pins.includes(p.id) ? 'Unpin' : 'Pin to Home'} title={pins.includes(p.id) ? 'Unpin' : 'Pin to Home'} data-testid={`home-project-pin-${p.id}`}>{pins.includes(p.id) ? <Pin className="h-4 w-4 fill-current" /> : <Pin className="h-4 w-4" />}</button>
      <button type="button" onClick={(e) => remove(e, p)} className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-destructive transition-colors" aria-label="Move to Trash" title="Move to Trash" data-testid={`home-project-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
    );
  };

  const ListBody = ({ list, empty, emptyTestId }) => {
    if (list === null) return <div className="space-y-2 py-1">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-md bg-muted animate-pulse" />)}</div>;
    if (!list.length) return <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid={emptyTestId}>{empty}</div>;
    return <div className="divide-y divide-border/60">{list.map((p) => <ProjectRow key={p.id} p={p} />)}</div>;
  };

  return (
    <div className="max-w-5xl" data-testid="backstage-home">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-foreground" data-testid="home-greeting">{greeting()}</h1>
          <p className="text-sm text-muted-foreground mt-1">Pick up where you left off, or start something new.</p>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5 rounded-md border border-border px-2.5 h-8" data-testid="home-current-publication">
          {state.projectId ? <Cloud className="h-3.5 w-3.5 text-primary" /> : <CloudOff className="h-3.5 w-3.5" />}
          <span className="font-medium text-foreground truncate max-w-[220px]">{state.projectName}</span>
          <span>· {state.projectId ? (hasUnsavedChanges ? 'unsaved changes' : 'saved') : 'not saved yet'}</span>
        </div>
      </div>

      {/* New */}
      <section className="mt-6" aria-labelledby="home-new-heading">
        <div className="flex items-center justify-between mb-2">
          <h2 id="home-new-heading" className="font-outfit text-base font-semibold text-foreground flex items-center gap-2"><FilePlus2 className="h-4 w-4 text-primary" /> New</h2>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => go('new')} data-testid="home-more-templates">More templates <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 [scrollbar-width:thin]" data-testid="home-new-strip">
          <NewCard label="Blank" sub={PAGE_PRESETS.letter.name.split(' (')[0]} onClick={() => createBlank('letter')} testId="home-new-blank-letter">
            <div className="bg-white shadow-sm ring-1 ring-black/10" style={{ width: 80, height: 104 }} />
          </NewCard>
          <NewCard label="Blank" sub="A4" onClick={() => createBlank('a4')} testId="home-new-blank-a4">
            <div className="bg-white shadow-sm ring-1 ring-black/10" style={{ width: 76, height: 108 }} />
          </NewCard>
          {templates.map(({ template, project }) => {
            const page = project.pages[0];
            const k = Math.min(104 / page.width, 104 / page.height);
            return (
              <NewCard key={template.id} label={template.name} sub={template.category} onClick={() => go('new', { templateId: template.id })} testId={`home-new-template-${template.id}`}>
                <div className="bg-white shadow-sm ring-1 ring-black/10 overflow-hidden" style={{ width: Math.round(page.width * k), height: Math.round(page.height * k) }}>
                  <StaticPage page={page} master={project.masters?.default} scale={k} />
                </div>
              </NewCard>
            );
          })}
          <NewCard label="More templates" sub={`${TEMPLATES.length} designs`} onClick={() => go('new')} testId="home-new-more">
            <LayoutTemplate className="h-8 w-8 text-primary" />
          </NewCard>
        </div>
      </section>

      {/* Folders — one tap into File ▸ Open filtered to that folder */}
      {projects && (folders.length > 0 || (projects.length > 0)) && (
        <section className="mt-6" aria-labelledby="home-folders-heading" data-testid="home-folders">
          <div className="flex items-center justify-between mb-2">
            <h2 id="home-folders-heading" className="font-outfit text-base font-semibold text-foreground flex items-center gap-2"><Folder className="h-4 w-4 text-primary" /> Folders</h2>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => go('open')} data-testid="home-manage-folders">{folders.length ? 'Manage folders' : 'New folder'} <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button>
          </div>
          {folders.length === 0 ? (
            <button type="button" onClick={() => go('open')} className="w-full rounded-lg border border-dashed border-border px-4 py-3 text-left text-sm text-muted-foreground hover:text-foreground hover:border-primary/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="home-folders-empty">
              Group your publications into folders — create the first one on the Open screen.
            </button>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 [scrollbar-width:thin]" data-testid="home-folder-strip">
              {topFolders.map((f) => {
                const subs = descendantIds(folders, f.id).size;
                return (
                  <button key={f.id} type="button" onClick={() => go('open', { folderId: f.id })} title={subs ? `${f.name} — ${subs} sub-folder${subs === 1 ? '' : 's'}` : f.name} className="group shrink-0 inline-flex items-center gap-2 h-10 rounded-lg border border-border bg-card pl-3 pr-3.5 text-sm hover:border-primary hover:shadow-sm transition-[border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`home-folder-${f.id}`}>
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: folderColorCss(f.color) }} aria-hidden="true" />
                    <span className="font-medium text-foreground max-w-[180px] truncate">{f.name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{deepCounts[f.id] || 0}</span>
                    {subs > 0 && <FolderTree className="h-3.5 w-3.5 text-muted-foreground" aria-label={`${subs} sub-folder${subs === 1 ? '' : 's'}`} data-testid={`home-folder-subfolders-${f.id}`} />}
                  </button>
                );
              })}
              {directCounts.unfiled > 0 && (
                <button type="button" onClick={() => go('open', { folderId: 'unfiled' })} className="shrink-0 inline-flex items-center gap-2 h-10 rounded-lg border border-dashed border-border bg-card/60 px-3 text-sm text-muted-foreground hover:text-foreground hover:border-primary/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="home-folder-unfiled">
                  <Inbox className="h-3.5 w-3.5" aria-hidden="true" />Unfiled <span className="text-xs tabular-nums">{directCounts.unfiled}</span>
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {/* Pinned / Recent */}
      <section className="mt-6" aria-label="Your publications">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <TabsList className="h-8" data-testid="home-lists-tabs">
              <TabsTrigger value="pinned" className="text-xs h-7" data-testid="home-tab-pinned"><Pin className="h-3.5 w-3.5 mr-1" /> Pinned {pinnedList ? `(${pinnedList.length})` : ''}</TabsTrigger>
              <TabsTrigger value="recent" className="text-xs h-7" data-testid="home-tab-recent"><Clock className="h-3.5 w-3.5 mr-1" /> Recent</TabsTrigger>
            </TabsList>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => go('open')} data-testid="home-more-publications">All publications <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button>
          </div>
          {error && <div className="text-sm text-destructive mb-2" data-testid="home-error">{error} <Button variant="link" size="sm" className="h-auto p-0 ml-1" onClick={load}>Retry</Button></div>}
          <TabsContent value="pinned" className="m-0" data-testid="home-pinned-list">
            <ListBody list={pinnedList} emptyTestId="home-pinned-empty" empty={<>Nothing pinned yet. Hover a recent publication and choose <b>Pin to Home</b> to keep it here.</>} />
          </TabsContent>
          <TabsContent value="recent" className="m-0" data-testid="home-recent-list">
            <ListBody list={recentList} emptyTestId="home-recent-empty" empty={<>No saved publications yet. Use <b>Save</b> to store your work in the cloud and it will show up here.</>} />
          </TabsContent>
        </Tabs>
      </section>

      {/* What changed lately — saved / shared / imported / copied / moved / deleted / restored */}
      <RecentActivity go={go} onOpen={(id) => open({ id })} />

      <div className="mt-6 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => go('open')} data-testid="home-open-file"><FolderOpen className="h-4 w-4 mr-2" /> Open</Button>
        <Button variant="outline" size="sm" onClick={() => go('trash')} data-testid="home-open-trash"><Trash2 className="h-4 w-4 mr-2" /> Trash</Button>
        <Button variant="outline" size="sm" onClick={() => go('import')} data-testid="home-import-pub"><Import className="h-4 w-4 mr-2" /> Import a Publisher file</Button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Save As                                                              */
/* ------------------------------------------------------------------ */

export const SaveAsSection = ({ onDone }) => {
  const { state } = useEditor();
  const { saveToCloud, downloadProjectFile, exportAs } = usePublicationActions();
  const [name, setName] = useState(state.projectId ? `${state.projectName} - Copy` : state.projectName);
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'cloud') { const saved = await saveToCloud({ name, asNew: true }); if (saved) onDone(); }
      else if (kind === 'json') downloadProjectFile(name);
      else await exportAs(kind, { scope: 'all', scale: 2, onProgress: setProgress, name });
    } finally { setBusy(null); }
  };
  return (
    <Section title="Save As" description="Save a copy of this publication under a new name, or save it in another format." testId="backstage-save-as">
      <div className="max-w-2xl space-y-5">
        <div className="space-y-1.5"><Label htmlFor="bs-saveas-name">File name</Label><Input id="bs-saveas-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="backstage-save-as-name-input" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <ActionCard icon={Save} tone="primary" title={state.projectId ? 'Save a copy to the cloud' : 'Save to the cloud'} description={state.projectId ? 'Creates a new publication and keeps the original untouched.' : 'Stores every page, master and guide so you can reopen it anywhere.'} onClick={() => run('cloud')} busy={busy === 'cloud'} disabled={!!busy || !name.trim()} testId="backstage-save-as-cloud" />
          <ActionCard icon={FileJson} title="WannaPublish project file (.json)" description="Download an editable copy to your computer." onClick={() => run('json')} busy={busy === 'json'} disabled={!!busy || !name.trim()} testId="backstage-save-as-json" />
          <ActionCard icon={FileText} title="PDF document" description="All pages in one file, 192 dpi." onClick={() => run('pdf')} busy={busy === 'pdf'} disabled={!!busy} testId="backstage-save-as-pdf" />
          <ActionCard icon={FileImage} title="PNG images" description="One lossless image per page." onClick={() => run('png')} busy={busy === 'png'} disabled={!!busy} testId="backstage-save-as-png" />
          <ActionCard icon={BookmarkPlus} title="Save as template" description="Reuse this design from File ▸ New ▸ My templates — in any colour scheme." onClick={() => setTemplateOpen(true)} disabled={!!busy} testId="backstage-save-as-template" />
        </div>
        {progress && <div className="text-xs text-muted-foreground" data-testid="backstage-save-as-progress">{progress}</div>}
      </div>
      <SaveAsTemplateDialog open={templateOpen} onOpenChange={setTemplateOpen} />
    </Section>
  );
};

/* ------------------------------------------------------------------ */
/* Print                                                                */
/* ------------------------------------------------------------------ */

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

export const PrintSection = () => {
  const { state } = useEditor();
  // Layout follows the view: a booklet shown as Two-Page Spread prints as spreads unless told otherwise
  const [opts, setOpts] = useState(() => ({ ...DEFAULT_PRINT_OPTIONS, layout: state.layoutMode === 'spread' && state.pages.length > 1 ? 'spreads' : 'pages' }));
  const [sheet, setSheet] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const set = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  const indices = useMemo(() => resolvePrintPages(state.pages, state.currentPageIndex, opts), [state.pages, state.currentPageIndex, opts]);
  // What actually goes on paper: one page per sheet, or two-page spreads (facing pages side by side)
  const sheets = useMemo(() => resolvePrintSheets(state.pages, state.currentPageIndex, opts), [state.pages, state.currentPageIndex, opts]);
  const layout = effectiveLayout(opts, state.pages.length);
  const spreads = layout === 'spreads';
  const booklet = layout === 'booklet';
  const bookletInfo = booklet ? sheets[0]?.booklet : null;
  const sheetWord = booklet ? 'side' : spreads ? 'spread' : 'page';
  const rangeInvalid = !booklet && opts.range === 'custom' && opts.customRange.trim() !== '' && parsePageRange(opts.customRange, state.pages.length).length === 0;
  useEffect(() => { setSheet((s) => Math.min(s, Math.max(0, sheets.length - 1))); }, [sheets.length]);

  const cur = useMemo(() => sheets[sheet] || sheets[0] || { kind: 'page', page: state.pages[0], width: state.pages[0].width, height: state.pages[0].height, label: 'Page 1', pageIndexes: [0] }, [sheets, sheet, state.pages]);
  // With printer's marks on, the rasterised sheet is the page plus a white bleed carrying the marks
  const marks = printMarks(opts, state.guides);
  const effPage = effectivePage(cur, opts, state.guides);
  const place = placementFor(effPage, opts);
  const foldCount = foldGuidesOf(state.guides).length;
  const previewRef = useRef(null);
  const [previewBox, setPreviewBox] = useState({ w: 420, h: 520 });
  useEffect(() => {
    if (!previewRef.current) return undefined;
    const ro = new ResizeObserver(([e]) => setPreviewBox({ w: Math.max(200, e.contentRect.width - 32), h: Math.max(260, Math.min(640, e.contentRect.height - 32)) }));
    ro.observe(previewRef.current);
    return () => ro.disconnect();
  }, []);
  const k = Math.min(previewBox.w / place.sheet.w, previewBox.h / place.sheet.h); // px per inch in the preview
  const copies = Math.max(1, Math.min(99, Math.round(opts.copies) || 1));
  const totalSheets = sheets.length * copies;

  // Live preview guides (screen only, never printed): bleed ring + trim line, ⅛ in safe area, 3× corner magnifier
  const [layers, setLayers] = useState(loadPreviewLayers);
  const toggleLayer = (key) => setLayers((l) => { const next = { ...l, [key]: !l[key] }; savePreviewLayers(next); return next; });
  const marksOn = !!marks;
  const bleedPx = marksOn ? pageRenderSize(cur, marks).bleed : 0;
  const bleedLabel = BLEED_OPTIONS.find((o) => o.value === opts.bleed)?.label || '⅜ in';
  const previewScale = place.w * k / effPage.width; // preview px per page px
  // Preflight every page on the sheet (a spread lists both pages, prefixed with the page name)
  const preflightMsgs = useMemo(() => {
    const sheetPages = cur.kind === 'spread' ? [cur.left, cur.right].filter(Boolean) : [cur.page];
    const per = sheetPages.map((pg) => preflightMessages(preflightPage(pg, pageMasterOf(state, pg), { bleed: bleedPx }), { marksOn, bleedLabel }));
    if (sheetPages.length === 1) return per[0];
    return sheetPages.flatMap((pg, i) => per[i].map((m) => ({ ...m, key: `${pg.id}-${m.key}`, text: `${pg.name}: ${m.text}` })));
  }, [cur, state, bleedPx, marksOn, bleedLabel]);

  const print = async () => {
    if (!booklet && !indices.length) { toast.error('Enter a valid page range, e.g. 1-3, 5.'); return; }
    setBusy('print');
    try {
      await printPublication({ pages: state.pages, masters: state.masters, textFlow: state.textFlow, guides: state.guides, currentPageIndex: state.currentPageIndex, title: state.projectName, options: opts, onProgress: (i, n) => setProgress(`Preparing ${sheetWord} ${i} of ${n}…`) });
      toast.success('Sent to the printer dialog');
    } catch (e) { console.error(e); toast.error(e?.message || 'Could not prepare the print job.'); }
    finally { setBusy(false); setProgress(''); }
  };

  // Same sheets, written straight to a PDF: the browser's print dialog (and its date / title / URL header and footer) is
  // never involved — open the file and print it from the PDF viewer.
  const savePdf = async () => {
    if (!booklet && !indices.length) { toast.error('Enter a valid page range, e.g. 1-3, 5.'); return; }
    setBusy('pdf');
    try {
      const { pdf, sheets: n } = await printToPdf({ pages: state.pages, masters: state.masters, textFlow: state.textFlow, guides: state.guides, currentPageIndex: state.currentPageIndex, options: opts, onProgress: (i, total) => setProgress(`Preparing ${sheetWord} ${i} of ${total}…`) });
      const name = `${(state.projectName || 'Publication').replace(/[\\/:*?"<>|]+/g, '-')} - print.pdf`;
      pdf.save(name);
      toast.success(`Saved “${name}”`, { description: `${n} ${n === 1 ? 'sheet' : 'sheets'}${booklet ? ' (booklet sides, in print order)' : spreads ? ' (two-page spreads)' : ''} at the chosen paper size — print it from your PDF viewer${booklet ? ' on both sides, flipping on the short edge' : ''}.` });
    } catch (e) { console.error(e); toast.error(e?.message || 'Could not build the PDF.'); }
    finally { setBusy(false); setProgress(''); }
  };

  const SelectField = ({ label, value, onChange, items, testId, hint }) => (
    <Field label={label} hint={hint}>
      <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger className="h-9" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent className="z-[90]">{items.map(([v, l]) => <SelectItem key={v} value={String(v)} data-testid={`${testId}-${v}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );

  return (
    <div className="max-w-6xl" data-testid="backstage-print">
      <h1 className="font-outfit text-2xl font-semibold text-foreground">Print</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">Choose what to print and how it sits on the paper, then send it to your printer.</p>
      <div className="grid gap-8 lg:grid-cols-[300px_1fr]">
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Button size="lg" className="h-12 px-5" onClick={print} disabled={!!busy || !indices.length} data-testid="print-button">{busy === 'print' ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Printer className="h-5 w-5 mr-2" />} Print</Button>
            <Field label="Copies">
              <Input type="number" min={1} max={99} value={opts.copies} onChange={(e) => set('copies', Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)))} className="h-9 w-20" data-testid="print-copies" />
            </Field>
          </div>
          <Button variant="outline" className="h-9" onClick={savePdf} disabled={!!busy || !indices.length} title="Write these sheets to a PDF file and print it from your PDF viewer — no browser header or footer" data-testid="print-save-pdf-button">{busy === 'pdf' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Save as PDF for printing</Button>
          {progress && <div className="text-xs text-muted-foreground" data-testid="print-progress">{progress}</div>}

          <div className="rounded-md border border-border bg-card p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="print-printer">
            <Printer className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
            <div><div className="text-foreground font-medium">Printer</div>Your browser's print dialog opens next — pick the printer, duplex and colour options there.</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="print-headers-hint">
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
            <div><div className="text-foreground font-medium">Date, title or web address on the printout?</div>Those are added by the browser, not by the publication. In the browser's print dialog open <em>More settings</em> and untick <em>Headers and footers</em> (Chrome / Edge) or set <em>Margins</em> to <em>None</em> — or use <em>Save as PDF for printing</em> above and print the file.</div>
          </div>

          <h2 className="font-outfit text-sm font-semibold text-foreground pt-1">Settings</h2>
          {state.pages.length > 1 && (
            <SelectField label="Layout" value={opts.layout} onChange={(v) => set('layout', v)} testId="print-layout" items={[['pages', 'One page per sheet'], ['spreads', 'Two-page spreads (facing pages side by side)'], ['booklet', 'Booklet (fold and staple)']]}
              hint={booklet && bookletInfo
                ? `Saddle stitch: ${state.pages.length} pages on ${bookletInfo.sheets} ${bookletInfo.sheets === 1 ? 'sheet' : 'sheets'} of ${(cur.width / 96).toFixed(1)} × ${(cur.height / 96).toFixed(1)} in paper, two pages per side${bookletInfo.blanks ? `, ${bookletInfo.blanks} blank ${bookletInfo.blanks === 1 ? 'page' : 'pages'} added so the count divides by 4` : ''}. Print on both sides, fold the stack once and staple at the spine.`
                : spreads ? `As in Two-Page Spread view: page 1 alone on the right, then 2–3, 4–5 … Each sheet is ${(cur.width / 96).toFixed(1)} × ${(cur.height / 96).toFixed(1)} in — pick a wide enough paper or “Fit to paper”.` : undefined} />
          )}
          {booklet && (
            <SelectField label="Booklet sides" value={opts.bookletSides} onChange={(v) => set('bookletSides', v)} testId="print-booklet-sides" items={[['both', 'Both sides, in order (two-sided printer)'], ['front', 'Fronts only (step 1 of printing by hand)'], ['back', 'Backs only (step 2 — reload the printed stack)']]}
              hint={opts.bookletSides === 'both' ? 'On a two-sided printer choose “flip on the short edge” so the backs come out the right way up.' : opts.bookletSides === 'front' ? 'Print the fronts, put the stack back in the tray the way your printer expects, then print the backs.' : 'Backs are in the same sheet order as the fronts: sheet 1 back, sheet 2 back …'} />
          )}
          {booklet
            ? <div className="space-y-1.5"><Label>Pages</Label><div className="h-9 rounded-md border border-border bg-muted/40 px-3 flex items-center text-sm text-muted-foreground" data-testid="print-range-booklet">All {state.pages.length} pages — a booklet is always printed whole</div></div>
            : <SelectField label="Pages" value={opts.range} onChange={(v) => set('range', v)} testId="print-range" items={[['all', `Print All Pages (${state.pages.length})`], ['current', spreads ? `Print Current Spread (${resolvePrintSheets(state.pages, state.currentPageIndex, { ...opts, range: 'current' })[0]?.label.toLowerCase() || `page ${state.currentPageIndex + 1}`})` : `Print Current Page (${state.currentPageIndex + 1})`], ['custom', 'Custom Range']]} hint={spreads && opts.range === 'custom' ? 'A spread is printed whenever either of its pages is in the range.' : undefined} />}
          {!booklet && opts.range === 'custom' && (
            <Field label="Page range" hint="Example: 1-3, 5, 8-">
              <Input value={opts.customRange} onChange={(e) => set('customRange', e.target.value)} placeholder="1-3, 5" className={`h-9 ${rangeInvalid ? 'border-destructive focus-visible:ring-destructive' : ''}`} aria-invalid={rangeInvalid} data-testid="print-custom-range" />
              {rangeInvalid && <p className="text-[11px] text-destructive" data-testid="print-range-error">No pages match this range (1–{state.pages.length}).</p>}
            </Field>
          )}
          {!booklet && <SelectField label="Sides" value={opts.sides} onChange={(v) => set('sides', v)} testId="print-sides" items={[['one', 'Print One Sided'], ['two', 'Print on Both Sides (flip on long edge)']]} hint={opts.sides === 'two' ? 'Two-sided printing is confirmed in the browser dialog under “Print on both sides”.' : undefined} />}
          <SelectField label="Paper" value={opts.paper} onChange={(v) => set('paper', v)} testId="print-paper" items={PAPER_SIZES.map((p) => [p.id, p.label])} />
          <SelectField label="Orientation" value={opts.orientation} onChange={(v) => set('orientation', v)} testId="print-orientation" items={[['auto', 'Match the page'], ['portrait', 'Portrait'], ['landscape', 'Landscape']]} />
          <SelectField label="Scaling" value={opts.scaling} onChange={(v) => set('scaling', v)} testId="print-scaling" items={[['actual', 'Actual size, centred'], ['fit', 'Fit to paper']]} hint={place.scale !== 1 && opts.scaling === 'actual' ? `The ${sheetWord} is larger than the paper and will be shrunk to ${Math.round(place.scale * 100)}%.` : undefined} />
          <SelectField label="Quality" value={opts.quality} onChange={(v) => set('quality', Number(v))} testId="print-quality" items={[[1, 'Draft (96 dpi)'], [2, 'Standard (192 dpi)'], [3, 'High (288 dpi)']]} />
          <Field label="Printer's marks" hint={opts.cropMarks || opts.foldMarks ? `A ${BLEED_OPTIONS.find((o) => o.value === opts.bleed)?.label || '⅜ in'} bleed with the marks is added around the page — choose a larger paper size so it still fits at actual size.` : foldCount ? `${foldCount} fold ${foldCount === 1 ? 'line' : 'lines'} available from the guides.` : 'Right-click a ruler guide and choose “Fold line” to add fold marks.'}>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer"><Checkbox checked={!!opts.cropMarks} onCheckedChange={(v) => set('cropMarks', !!v)} data-testid="print-crop-marks" /> Crop marks</label>
              <label className={`flex items-center gap-2 text-sm cursor-pointer ${foldCount ? 'text-foreground' : 'text-muted-foreground'}`}><Checkbox checked={!!opts.foldMarks} onCheckedChange={(v) => set('foldMarks', !!v)} data-testid="print-fold-marks" /> Fold marks</label>
              <div className={`flex items-center gap-2 text-sm ${opts.cropMarks || opts.foldMarks ? 'text-foreground' : 'text-muted-foreground'}`} role="radiogroup" aria-label="Bleed width" data-testid="print-bleed">
                <span>Bleed</span>
                {BLEED_OPTIONS.map((o) => <button key={o.value} type="button" role="radio" aria-checked={opts.bleed === o.value} title={o.hint} disabled={!(opts.cropMarks || opts.foldMarks)} onClick={() => set('bleed', o.value)} className={`h-7 px-2 rounded-md border text-xs transition-colors disabled:opacity-50 ${opts.bleed === o.value ? 'border-primary bg-primary/10 text-foreground' : 'border-border hover:bg-muted'}`} data-testid={`print-bleed-${o.value}`}>{o.label}</button>)}
              </div>
            </div>
          </Field>
        </div>

        {/* Preview */}
        <div className="min-w-0">
          <div ref={previewRef} className="rounded-lg border border-border bg-muted/40 p-4 min-h-[420px] lg:min-h-[560px] flex flex-col" data-testid="print-preview">
            {/* Guides toolbar: what the tints and dashed lines mean; none of it prints */}
            <div className="flex flex-wrap items-center gap-2 pb-3" data-testid="print-preview-toolbar">
              <span className="text-xs font-medium text-foreground mr-1">Show</span>
              {[['bleed', marksOn ? 'Bleed & trim' : 'Trim line', 'Tint the bleed that gets cut off and draw the trim line — screen only'], ['safe', 'Safe area', 'Dashed ⅛ in margin inside the trim: keep text inside it — screen only'], ['magnify', 'Magnify corner', 'A 3× close-up of the top-left corner so you can check the marks']].map(([key, label, tip]) => (
                <button key={key} type="button" role="switch" aria-checked={!!layers[key]} title={tip} onClick={() => toggleLayer(key)} className={`h-7 px-2.5 rounded-md border text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${layers[key] ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`} data-testid={`print-preview-toggle-${key}`}>{label}</button>
              ))}
              <span className="ml-auto"><PreviewLegend layers={layers} marksOn={marksOn} /></span>
            </div>
            <div className="flex-1 flex items-center justify-center relative">
              {sheets.length ? (
                <div className="relative bg-white shadow-lg ring-1 ring-black/10" style={{ width: Math.round(place.sheet.w * k), height: Math.round(place.sheet.h * k) }} data-testid="print-preview-sheet" data-layout={layout} aria-label={`Sheet ${sheet + 1} preview`}>
                  <div className="absolute overflow-hidden ring-1 ring-black/10" style={{ left: Math.round(place.x * k), top: Math.round(place.y * k), width: Math.round(place.w * k), height: Math.round(place.h * k) }} data-testid="print-preview-page">
                    <SheetView sheet={cur} masters={state.masters} textFlow={state.textFlow} marks={marks} guides={state.guides} scale={previewScale} />
                    {(layers.bleed || layers.safe) && <PrintPreviewGuides pageW={cur.width} pageH={cur.height} bleed={bleedPx} s={previewScale} layers={layers} bleedLabel={bleedLabel} />}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground" data-testid="print-preview-empty">Enter a valid page range to see a preview.</div>
              )}
              {sheets.length > 0 && layers.magnify && (
                <div className="absolute right-0 top-0 w-[176px] rounded-md border border-border bg-card shadow-lg overflow-hidden" data-testid="print-preview-magnifier">
                  <div className="relative bg-white" style={{ width: 176, height: 150, overflow: 'hidden' }}>
                    <div className="absolute left-0 top-0">
                      <SheetView sheet={cur} masters={state.masters} textFlow={state.textFlow} marks={marks} guides={state.guides} scale={previewScale * 3} />
                      {(layers.bleed || layers.safe) && <PrintPreviewGuides pageW={cur.width} pageH={cur.height} bleed={bleedPx} s={previewScale * 3} layers={layers} bleedLabel={bleedLabel} showLabels={false} />}
                    </div>
                  </div>
                  <div className="px-2 py-1 text-[10px] text-muted-foreground border-t border-border">Top-left corner · 3× · {marksOn ? `crop marks sit ${bleedLabel} outside the trim` : 'no printer’s marks'}</div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-center gap-3 pt-3 text-xs text-muted-foreground">
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setSheet((s) => Math.max(0, s - 1))} disabled={sheet <= 0} aria-label="Previous sheet" data-testid="print-preview-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span data-testid="print-preview-status">{sheets.length ? `${cur.label} · sheet ${sheet + 1} of ${sheets.length}` : 'No pages'}{copies > 1 ? ` · ${copies} copies (${totalSheets} sheets)` : ''}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setSheet((s) => Math.min(sheets.length - 1, s + 1))} disabled={sheet >= sheets.length - 1} aria-label="Next sheet" data-testid="print-preview-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <div className="text-center text-[11px] text-muted-foreground pt-1" data-testid="print-preview-paper">Paper {place.sheet.w.toFixed(2)} × {place.sheet.h.toFixed(2)} in · {sheetWord} {(cur.width / 96).toFixed(2)} × {(cur.height / 96).toFixed(2)} in{marks ? ` + bleed (${(effPage.width / 96).toFixed(2)} × ${(effPage.height / 96).toFixed(2)} in)` : ''}{place.scale !== 1 ? ` at ${Math.round(place.scale * 100)}%` : ''}</div>
          </div>
          {/* Preflight: what to expect at the edges of this page */}
          {sheets.length > 0 && (
            <ul className="mt-3 rounded-md border border-border bg-card divide-y divide-border" data-testid="print-preflight">
              {preflightMsgs.map((m) => (
                <li key={m.key} className="flex items-start gap-2 px-3 py-2 text-xs text-muted-foreground" data-testid={`print-preflight-item-${m.key}`} data-kind={m.kind}>
                  {m.kind === 'warn' ? <AlertTriangle className="h-4 w-4 shrink-0 mt-px text-amber-600 dark:text-amber-400" aria-hidden="true" /> : m.kind === 'ok' ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-px text-emerald-600 dark:text-emerald-400" aria-hidden="true" /> : <Info className="h-4 w-4 shrink-0 mt-px text-primary" aria-hidden="true" />}
                  <span className="text-foreground/90">{m.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Share                                                                */
/* ------------------------------------------------------------------ */

export const ShareSection = ({ go }) => {
  const { state, hasUnsavedChanges } = useEditor();
  const { saveToCloud, exportAs } = usePublicationActions();
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  const [progress, setProgress] = useState('');
  // Sharing is opt-in per publication: null = not checked yet, '' = saved but not shared, otherwise the share token.
  const [shareToken, setShareToken] = useState(null);
  const projectId = state.projectId;

  useEffect(() => {
    if (!projectId) { setShareToken(null); return undefined; }
    let alive = true;
    getShare(projectId).then((info) => { if (alive) setShareToken(info.share_token || ''); }).catch(() => { if (alive) setShareToken(''); });
    return () => { alive = false; };
  }, [projectId]);

  const shareLink = shareToken ? shareUrlFor(shareToken) : null;

  const ensureSaved = async () => {
    setBusy('save');
    try { return await saveToCloud({}); } finally { setBusy(null); }
  };
  /** Save first if needed, then switch sharing on for this publication. */
  const startSharing = async () => {
    setBusy('share');
    try {
      let id = projectId;
      if (!id) {
        const saved = await saveToCloud({});
        if (!saved) return;
        id = saved.id;
      }
      const info = await enableShare(id);
      setShareToken(info.share_token || '');
      toast.success('Sharing is on', { description: 'Anyone with the link who signs in gets their own copy of this publication.' });
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'Could not create the share link.'));
    } finally { setBusy(null); }
  };
  const stopSharing = async () => {
    setBusy('share');
    try {
      await disableShare(projectId);
      setShareToken('');
      toast.success('Sharing is off', { description: 'The old link no longer opens this publication.' });
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'Could not stop sharing.'));
    } finally { setBusy(null); }
  };
  const copy = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch {
      const el = document.getElementById('share-link-input');
      el?.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
    }
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };
  const emailLink = () => {
    const subject = encodeURIComponent(`${state.projectName} — shared with WannaPublish`);
    const body = encodeURIComponent(`Hi,\n\nI'd like to share my publication “${state.projectName}” with you.\n\nOpen it here: ${shareLink}\n\n— sent from WannaPublish`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const emailPdf = async () => {
    setBusy('email-pdf');
    try {
      const ok = await exportAs('pdf', { scope: 'all', scale: 2, onProgress: setProgress });
      if (ok) {
        const subject = encodeURIComponent(state.projectName);
        const body = encodeURIComponent(`Hi,\n\nPlease find “${state.projectName}” attached as a PDF.\n\n— sent from WannaPublish`);
        toast.info('The PDF was downloaded — attach it to the email that opens next.');
        setTimeout(() => { window.location.href = `mailto:?subject=${subject}&body=${body}`; }, 600);
      }
    } finally { setBusy(null); }
  };
  const run = async (kind) => { setBusy(kind); try { await exportAs(kind, { scope: 'all', scale: 2, onProgress: setProgress }); } finally { setBusy(null); } };

  return (
    <Section title="Share" description="Send a link to your saved publication, email it, or publish it as a PDF." testId="backstage-share">
      <div className="max-w-2xl space-y-6">
        <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="share-people-card">
          <div className="flex items-center gap-2 font-medium text-foreground"><Link2 className="h-4 w-4 text-primary" /> Share with People</div>
          {shareLink ? (
            <>
              <p className="text-xs text-muted-foreground">Anyone with this link who signs in to WannaPublish gets their own copy of the latest saved version of “{state.projectName}”. Your original stays yours.</p>
              <div className="flex gap-2">
                <Input id="share-link-input" readOnly value={shareLink} onFocus={(e) => e.target.select()} className="h-9 font-mono text-xs" data-testid="share-link-input" />
                <Button onClick={copy} className="h-9 shrink-0" data-testid="share-copy-link">{copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />} {copied ? 'Copied' : 'Copy link'}</Button>
                <Button variant="outline" className="h-9 shrink-0" asChild><a href={shareLink} target="_blank" rel="noreferrer" aria-label="Open link in a new tab" data-testid="share-open-link"><ExternalLink className="h-4 w-4" /></a></Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {hasUnsavedChanges && (
                  <span className="flex items-center gap-2" data-testid="share-unsaved-hint">
                    <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--wp-context-amber))]" /> You have unsaved changes — the link opens the last saved version.
                    <Button variant="link" size="sm" className="h-auto p-0" onClick={ensureSaved} disabled={busy === 'save'} data-testid="share-save-changes">{busy === 'save' ? 'Saving…' : 'Save now'}</Button>
                  </span>
                )}
                <Button variant="link" size="sm" className="h-auto p-0 text-muted-foreground hover:text-destructive" onClick={stopSharing} disabled={busy === 'share'} data-testid="share-stop">{busy === 'share' ? 'Stopping…' : 'Stop sharing'}</Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {projectId ? 'This publication is private. Turn sharing on to get a link — people who open it sign in and receive their own copy.' : 'Save the publication to the cloud first — sharing then creates a link that opens a copy in WannaPublish.'}
              </p>
              <Button onClick={startSharing} disabled={!!busy || (shareToken === null && !!projectId)} data-testid={projectId ? 'share-start' : 'share-save-first'}>
                {busy === 'share' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : projectId ? <Link2 className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {projectId ? 'Create a share link' : 'Save to the cloud and create a link'}
              </Button>
            </>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="share-email-card">
          <div className="flex items-center gap-2 font-medium text-foreground"><Mail className="h-4 w-4 text-primary" /> Email</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <ActionCard icon={Link2} title="Send a link" description={shareLink ? 'Opens your email app with the link in the message.' : 'Save to the cloud first to get a link.'} onClick={emailLink} disabled={!shareLink || !!busy} testId="share-email-link" />
            <ActionCard icon={FileText} title="Send as PDF attachment" description="Downloads a PDF, then opens your email app so you can attach it." onClick={emailPdf} busy={busy === 'email-pdf'} disabled={!!busy} testId="share-email-pdf" />
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="share-publish-card">
          <div className="flex items-center gap-2 font-medium text-foreground"><FileText className="h-4 w-4 text-primary" /> Publish as PDF or Images</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <ActionCard icon={FileText} title="Create PDF" description="All pages, 192 dpi." onClick={() => run('pdf')} busy={busy === 'pdf'} disabled={!!busy} testId="share-create-pdf" />
            <ActionCard icon={FileImage} title="PNG images" description="One image per page." onClick={() => run('png')} busy={busy === 'png'} disabled={!!busy} testId="share-create-png" />
            <ActionCard icon={ChevronRight} title="More export options" description="Quality, current page only, JPEG…" onClick={() => go('export')} disabled={!!busy} testId="share-more-export" />
          </div>
          {progress && <div className="text-xs text-muted-foreground" data-testid="share-progress">{progress}</div>}
        </div>
      </div>
    </Section>
  );
};

/* ------------------------------------------------------------------ */
/* Close                                                                */
/* ------------------------------------------------------------------ */

/**
 * "Do you want to save changes?" — shown by the Close command when the document has unsaved changes.
 * Save → cloud save (asks for a name when the publication was never saved) then close; Don't Save → close; Cancel.
 */
export const ClosePublicationDialog = ({ open, onOpenChange, onClosed }) => {
  const { state } = useEditor();
  const { saveToCloud, closePublication } = usePublicationActions();
  const [name, setName] = useState(state.projectName);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(state.projectName); }, [open, state.projectName]);

  const finish = () => { onOpenChange(false); closePublication(); onClosed && onClosed(); };
  const saveAndClose = async () => {
    setBusy(true);
    try {
      const saved = await saveToCloud({ name });
      if (saved) finish();
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="z-[100] sm:max-w-[440px] bg-card text-card-foreground border-border shadow-xl" overlayClassName="z-[95] bg-black/40 backdrop-blur-sm" data-testid="close-publication-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-outfit">Save changes to “{state.projectName}”?</AlertDialogTitle>
          <AlertDialogDescription>Your latest edits have not been saved. If you close without saving, they will be lost.</AlertDialogDescription>
        </AlertDialogHeader>
        {!state.projectId && (
          <div className="space-y-1.5">
            <Label htmlFor="close-save-name">Publication name</Label>
            <Input id="close-save-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="close-publication-name" />
          </div>
        )}
        <AlertDialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={finish} disabled={busy} data-testid="close-publication-dont-save">Don't Save</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="close-publication-cancel">Cancel</Button>
            <Button onClick={saveAndClose} disabled={busy || !name.trim()} data-testid="close-publication-save">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save</Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
